import { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Sheet,
  SyncIcon,
  WarningIcon,
  LinkUserIcon,
  SwapIcon,
  AtLogoIcon,
  LinkCheckIcon,
  LinkConnectorInIcon,
  LinkConnectorOutIcon,
} from '@esign/ui'
import { errorText } from '../fields.js'
import type { StepProps } from '../types.js'

export function LinkAccountStep({ step, error, notice, busy, submit }: StepProps<'link_account'>) {
  const [dismissed, setDismissed] = useState(false)

  const start = () => {
    setDismissed(false)
    submit({})
  }

  const showSheet = error && !dismissed
  const unknownError = error && error.code !== 'PHONE_MISMATCH' && error.code !== 'ACCOUNT_NOT_MATCHED'

  const oldPhone = String(error?.data?.['old_phone'] ?? '******.789')
  const newPhone = String(error?.data?.['new_phone'] ?? '0123.456.789')

  // Figma 1:65097 — confirmation before leaving for eKYC
  if (notice?.code === 'LINKED') {
    return (
      <Card className="link-step-card">
        <LinkGraphic variant="success" />
        <p className="link-step-description link-step-description--success">
          Liên kết tài khoản
          <br />
          thành công
        </p>
        <div className="link-step-action link-step-action--wide">
          <Button disabled={busy} onClick={() => submit({ proceed: true })}>
            Xác thực danh tính
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card className="link-step-card">
        {unknownError && <Alert>{errorText(error.code)}</Alert>}

        <LinkGraphic variant="pending" />

        <p className="link-step-description">
          Để nhận thanh toán, bạn “bắt buộc” phải liên kết tài khoản với hệ thống của đối tác
        </p>

        <div className="link-step-action">
          <Button disabled={busy} onClick={start}>
            {busy ? 'Đang liên kết…' : 'Liên kết ngay'}
          </Button>
        </div>
      </Card>

      {showSheet && error.code === 'PHONE_MISMATCH' && (
        <Sheet
          title="Đồng bộ thông tin tài khoản"
          icon={<SyncIcon />}
          onClose={() => setDismissed(true)}
          body={
            <div className="mismatch-body">
              <p className="mismatch-alert-text">
                Hệ thống phát hiện số điện thoại trên hệ thống đối tác không trùng khớp với số điện thoại hiện tại của bạn
              </p>
              <div className="mismatch-table">
                <div className="mismatch-row">
                  <span className="mismatch-label">SĐT Cũ:</span>
                  <span className="mismatch-value mismatch-value--muted">{oldPhone}</span>
                </div>
                <div className="mismatch-row">
                  <span className="mismatch-label">SĐT Mới:</span>
                  <span className="mismatch-value mismatch-value--highlight">{newPhone}</span>
                </div>
              </div>
              <p className="mismatch-question">
                Bạn muốn cập nhật số điện thoại ở hệ thống đối tác thành số điện thoại hiện tại là{' '}
                <strong>{newPhone}</strong> không?
              </p>
            </div>
          }
        >
          <div className="mismatch-buttons">
            <Button variant="ghost" disabled={busy} onClick={() => submit({ resolution: 'keep' })}>
              Giữ nguyên
            </Button>
            <Button disabled={busy} onClick={() => submit({ resolution: 'update' })}>
              Cập nhật
            </Button>
          </div>
        </Sheet>
      )}

      {showSheet && error.code === 'ACCOUNT_NOT_MATCHED' && (
        <Sheet
          title={`Thông tin tài khoản của bạn không trùng khớp với hệ thống đối tác ${step.config.target_system || 'ACCESSTRADE'}`}
          icon={<div className="warning-icon-wrapper"><WarningIcon /></div>}
          onClose={() => setDismissed(true)}
        >
          {step.config.support_url ? (
            <Button onClick={() => location.assign(step.config.support_url!)}>Liên hệ trợ giúp</Button>
          ) : (
            <Button onClick={() => setDismissed(true)}>Đóng</Button>
          )}
        </Sheet>
      )}
    </>
  )
}

type LinkGraphicProps = {
  variant?: 'pending' | 'success'
}

/**
 * Figma link_account graphic:
 * - pending  → node 1:65096 (swap + AT logo)
 * - success  → node 1:65097 (green check + AT logo)
 */
export function LinkGraphic({ variant = 'pending' }: LinkGraphicProps) {
  const success = variant === 'success'

  return (
    <div className={`link-graph${success ? ' link-graph--success' : ''}`} aria-hidden="true">
      <div className="link-graph__node">
        <div className="link-graph__circle link-graph__circle--user">
          <LinkUserIcon />
        </div>
      </div>

      <div className="link-graph__connector link-graph__connector--in">
        <LinkConnectorInIcon />
      </div>

      <div className="link-graph__node">
        <div className={`link-graph__sync${success ? ' link-graph__sync--success' : ''}`}>
          <span className="link-graph__sync-ring link-graph__sync-ring--outer" />
          <span className="link-graph__sync-ring link-graph__sync-ring--inner" />
          <span className="link-graph__sync-face">
            {success ? <LinkCheckIcon /> : <SwapIcon className="link-graph__swap-icon" />}
          </span>
        </div>
      </div>

      <div className="link-graph__connector link-graph__connector--out">
        <LinkConnectorOutIcon />
      </div>

      <div className="link-graph__node">
        <div className="link-graph__circle link-graph__circle--partner">
          <AtLogoIcon />
        </div>
      </div>
    </div>
  )
}
